import { Component, Injector, OnInit, computed, forwardRef, input, model, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { DxDateBoxComponent, DxDateBoxModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/date_box';
import { DatetimeType } from 'src/app/controls/models/datetime-type.model';
import { DateTimeService } from 'src/app/shared/datetime/datetime.service';
import { LayoutControlComponent } from '../../internal/layout-control/layout-control.component';
import { ControlLabelComponent } from '../../internal/control-label/control-label.component';
import { CalculateValidationTextFn, ControlSupportingTextComponent } from '../../internal/control-supporting-text/control-supporting-text.component';

@Component({
  selector: 'app-datetimebox',
  templateUrl: './datetimebox.component.html',
  styleUrls: ['./datetimebox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimeboxComponent),
      multi: true
    }, {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatetimeboxComponent),
      multi: true,
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LayoutControlComponent, ControlLabelComponent, DxDateBoxModule, ControlSupportingTextComponent]
})
export class DatetimeboxComponent implements OnInit, ControlValueAccessor, Validator {
  private dx = viewChild.required<DxDateBoxComponent>('dx');
  private ngControl: NgControl = null;

  type = model<DatetimeType>(DatetimeType.DateTime);
  value = model<Date | null>(null);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);
  caption = input.required<string>();
  helpText = input<string>('');
  timeInterval = input<number>(15);
  calculateValidationText = input<CalculateValidationTextFn | string>();

  computedValue = computed<Date | null>(() => this.value() ? new Date(this.value()) : null);

  constructor(
    private injector: Injector,
    private dateTimeService: DateTimeService,
  ) { }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl);
  }

  get control(): AbstractControl {
    return this.ngControl?.control;
  }

  get isValid(): boolean {
    return !this.control || (this.control.invalid !== true || (this.control.pristine && this.control.untouched));
  }

  onValueChanged(e: ValueChangedEvent): void {
    if (e.value !== this.computedValue()) {
      this.onChanged(e.value);
      this.value.set(e.value);
    }
  }

  displayFormat = computed(() => {
    switch (this.type()) {
      case DatetimeType.Date:
        return this.dateTimeService.getDevExpressDateFunction;
      case DatetimeType.Time:
        return this.dateTimeService.formatTime;
      case DatetimeType.DateTime:
      default:
        return this.dateTimeService.getDevExpressDateTimeFunction;
    }
  });


  /*
   * Validator interface methods
   */

  validate(control: AbstractControl): ValidationErrors {
    if (control instanceof AbstractControl) {
      if (this.dx().validationError) {
        const err = this.dx().validationError;
        return { dateRange: err?.message };
      }
    }
    return null;
  }

  onValidatorChange: () => void = () => { };
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: any): void {
    this.value.set(obj);
  }

  onChanged: (value: any) => void = () => { };
  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.dx().disabled = isDisabled;
  }
}
